import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CheckCircle } from 'lucide-react';
import './EnquiryModal.css';

export default function EnquiryModal({ isOpen, onClose, source = 'Website' }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Here you would normally send the form data to your backend
      const enquiryData = {
        ...formData,
        source: source,
        timestamp: new Date().toISOString(),
      };

      // For now, log to console (replace with actual API call)
      console.log('Enquiry submitted:', enquiryData);

      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1000));

      setSubmitted(true);
      setTimeout(() => {
        setSubmitted(false);
        setFormData({ name: '', email: '', phone: '', company: '', message: '' });
        onClose();
      }, 2000);
    } catch (error) {
      console.error('Error submitting enquiry:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className="enquiry-modal__backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            className="enquiry-modal"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.3 }}
          >
            <div className="enquiry-modal__header">
              <h2 className="enquiry-modal__title">Get Started</h2>
              <button
                className="enquiry-modal__close"
                onClick={onClose}
                aria-label="Close modal"
              >
                <X size={20} />
              </button>
            </div>

            {submitted ? (
              <motion.div
                className="enquiry-modal__success"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <div className="enquiry-modal__success-icon"><CheckCircle size={48} strokeWidth={1.5} /></div>
                <h3 className="enquiry-modal__success-title">Thank You!</h3>
                <p className="enquiry-modal__success-message">
                  We have received your enquiry and will get back to you shortly.
                </p>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} className="enquiry-modal__form">
                <div className="enquiry-modal__group">
                  <label htmlFor="name" className="enquiry-modal__label">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="enquiry-modal__input"
                    placeholder="Your name"
                  />
                </div>

                <div className="enquiry-modal__row">
                  <div className="enquiry-modal__group">
                    <label htmlFor="email" className="enquiry-modal__label">
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="enquiry-modal__input"
                      placeholder="your@email.com"
                    />
                  </div>

                  <div className="enquiry-modal__group">
                    <label htmlFor="phone" className="enquiry-modal__label">
                      Phone
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="enquiry-modal__input"
                      placeholder="+1 (555) 000-0000"
                    />
                  </div>
                </div>

                <div className="enquiry-modal__group">
                  <label htmlFor="company" className="enquiry-modal__label">
                    Company
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    className="enquiry-modal__input"
                    placeholder="Your company"
                  />
                </div>

                <div className="enquiry-modal__group">
                  <label htmlFor="message" className="enquiry-modal__label">
                    Tell us more
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    className="enquiry-modal__textarea"
                    placeholder="What services are you interested in?"
                    rows="4"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="enquiry-modal__submit"
                >
                  {loading ? 'Sending...' : 'Send Enquiry'}
                </button>

                <p className="enquiry-modal__note">
                  We'll be in touch within 24 hours.
                </p>
              </form>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
